import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { ShiftHours } from 'utils/enums';

dayjs.extend(duration);
dayjs.extend(utc);
dayjs.extend(timezone);

export function calculateUtilization(
  totalEngineActiveMinutes: number,
  totalEngineWorkingMinutes: number
) {
  if (totalEngineActiveMinutes === 0 && totalEngineWorkingMinutes === 0) {
    return '0.00';
  }

  if (totalEngineActiveMinutes === 0) {
    return 'N/A';
  }

  const utilization =
    (totalEngineWorkingMinutes / totalEngineActiveMinutes) * 100;
  return utilization.toFixed(2);
}

export function calculatePercentageDifference(
  totalEngineActiveMinutes: number,
  totalEngineWorkingMinutes: number,
  totalEngineActiveMinutesDiff: number,
  totalEngineWorkingMinutesDiff: number
) {
  const currentUtilization =
    (totalEngineWorkingMinutes / totalEngineActiveMinutes) * 100;

  const previousActiveMinutes =
    totalEngineActiveMinutes / (1 + totalEngineActiveMinutesDiff / 100);
  const previousWorkingMinutes =
    totalEngineWorkingMinutes / (1 + totalEngineWorkingMinutesDiff / 100);

  const previousUtilization =
    (previousWorkingMinutes / previousActiveMinutes) * 100;

  const utilizationDiff =
    ((currentUtilization - previousUtilization) / previousUtilization) * 100;

  return utilizationDiff;
}

/**
 * Convert nanoseconds to minutes using Day.js
 * @param nanoseconds - The time in nanoseconds to be converted
 * @returns The time converted to minutes (formatted to 2 decimal places)
 */
export function convertNanosecondsToMinutes(nanoseconds: number): number {
  const milliseconds = nanoseconds / 1e6;

  const minutes = dayjs.duration(milliseconds, 'milliseconds').asMinutes();

  return parseFloat(minutes.toFixed(2));
}

/**
 * Convert minutes to minutes using Day.js
 * @param minutes - The time in nanoseconds to be converted
 * @returns The time converted to minutes (formatted to 2 decimal places)
 */
export function convertMinutesToHours(minutes: number): number {
  const hours = dayjs.duration(minutes, 'minutes').asHours();

  return parseFloat(hours.toFixed(2));
}

/**
 * Convert nanoseconds to hours using Day.js
 * @param nanoseconds - The time in nanoseconds to be converted
 * @returns The time converted to hous (formatted to 2 decimal places)
 */
export function convertNanosecondsToHours(nanoseconds: number): number {
  const milliseconds = nanoseconds / 1e6;

  const hours = dayjs.duration(milliseconds, 'milliseconds').asHours();
  return parseFloat(hours.toFixed(2));
}

/**
 * Convert seconds to hours using Day.js
 * @param seconds  - The time in seconds to be converted
 * @returns The time converted to hous (formatted to 2 decimal places)
 */
export function convertsecondsToHours(seconds: number): number {
  const hours = dayjs.duration(seconds, 'seconds').asHours();
  return parseFloat(hours.toFixed(2));
}

export function isSumZeroOrEmpty(array: any[], field: string) {
  if (array.length === 0) return true;

  const sum = array.reduce(
    (acc: number, obj) => acc + (Number(obj[field]) || 0),
    0
  );

  return sum === 0;
}

export function getDataUnit(type: string, unitSystem: string) {
  type = type.toLowerCase().replace(/ /g, '');
  if (type === 'production') {
    return 'Tons';
  }

  if (type === 'fuel') {
    return unitSystem === 'imperial' ? 'gal' : 'l';
  }

  if (type === 'utilization') {
    return '%';
  }

  if (type === 'idlinghours') {
    return 'hrs';
  }

  if (type === 'productivehours') {
    return 'hrs';
  }
  if (type === 'avgfuelrate') {
    return unitSystem === 'imperial' ? 'gal/h' : 'l/h';
  }
  if (type === 'enginehours') {
    return 'hrs';
  }
  if (type === 'productionrate') {
    return 'Tons / h';
  }
  if (type === 'distance') {
    return unitSystem === 'imperial' ? 'Miles' : 'Kms';
  }
}

export const getColor = (isMin?: boolean) => {
  const danger = `#FF7C54`;
  const success = `#1B9D59`;
  if (isMin) {
    return danger;
  } else {
    return success;
  }
};

export function daysLeftInYear() {
  const today: any = new Date();
  const endOfYear: any = new Date(today.getFullYear(), 11, 31);
  const timeDifference = endOfYear - today;
  const daysLeft = Math.ceil(timeDifference / (1000 * 60 * 60 * 24));
  return daysLeft;
}

export function filterDataByPeriod(
  dataArray: Array<{
    period: string;
    date: string;
    total_engine_active_minutes: string;
    total_engine_working_minutes: string;
  }>,
  period: string
) {
  const today = new Date();

  let startDate: Date;
  switch (period) {
    case '7days':
      startDate = new Date(today.setDate(today.getDate() - 8));
      break;
    case '14days':
      startDate = new Date(today.setDate(today.getDate() - 15));
      break;
    case '1month':
      startDate = new Date(today.setDate(today.getDate() - 30));
      break;
    case '6months':
      startDate = new Date(today.setMonth(today.getMonth() - 6));
      break;
    default:
      startDate = new Date(today.setDate(today.getDate() - 8));
  }

  return dataArray
    .filter(
      (item) => new Date(item.date) >= startDate && item.period === period
    )
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
}

export const getShiftHours = (customerCode: string) => {
  return ShiftHours[customerCode] || ShiftHours.others;
};

export const ShiftDetails: Record<
  string,
  Array<{ startHour: number; endHour: number }>
> = {
  mop: [
    { startHour: 6, endHour: 18 },
    { startHour: 18, endHour: 30 },
  ],
  buz: [{ startHour: 5, endHour: 16 }],
  myn: [{ startHour: 5, endHour: 16 }],
  drn: [
    { startHour: 6, endHour: 18 },
    { startHour: 18, endHour: 30 },
  ],
  car: [
    { startHour: 7.5, endHour: 19.5 },
    { startHour: 19.5, endHour: 31.5 },
  ],
  others: [{ startHour: 0, endHour: 20 }],
};

export const isMultiShiftCustomer = (customerCode: string) => {
  return (ShiftDetails[customerCode] || []).length > 1;
};

export interface ShiftDetail {
  startHour: number;
  endHour: number;
  daysToAdd: number;
  endTimeHour: number;
  shiftName?: string;
}
/**
 * Retrieves shift details for a given client ID, calculating days to add
 * and the ending hour of the shift for accurate shift timing. If the client ID
 * does not have any shifts or is not found, the default shift from "others" is returned.
 *
 * @param {string} clientId - The identifier for the client whose shift details need to be retrieved.
 * @returns {Array<Object>} Returns:
 *   - If shifts exist for the client ID: An array of shift details, where each object contains:
 *      - {number} startHour - The hour in UTC when the shift starts.
 *      - {number} endHour - The hour in UTC when the shift ends.
 *      - {number} daysToAdd - The number of days to add for the shift to end.
 *      - {number} endTimeHour - The hour of the day (0–23) when the shift ends.
 *   - If no shifts exist: An array with an object with the default shift details from "others" including:
 *      - {number} startHour - The default start hour in UTC.
 *      - {number} endHour - The default end hour in UTC.
 *      - {number} daysToAdd - Always 0 for the default case.
 *      - {number} endTimeHour - Same as the default end hour.
 *
 * @example
 * // Example with existing client ID
 * const clientId = "mop";
 * const result = getShiftDetails(clientId);
 * console.log(result);
 * // Output:
 * // [
 * //   { startHour: 4, endHour: 16, daysToAdd: 0, endTimeHour: 16 },
 * //   { startHour: 16, endHour: 28, daysToAdd: 1, endTimeHour: 4 }
 * // ]
 *
 * @example
 * // Example with missing client ID
 * const clientId = "unknown";
 * const result = getShiftDetails(clientId);
 * console.log(result);
 * // Output:
 * // { startHour: 0, endHour: 20, daysToAdd: 0, endTimeHour: 20 }
 */
export const getShiftDetails = (
  clientId: string,
  shiftDetails?: Array<{
    startHour: number;
    endHour: number;
    shiftName?: string;
  }>
): ShiftDetail[] => {
  const shifts = (shiftDetails ?? ShiftDetails[clientId]) as Array<{
    startHour: number;
    endHour: number;
    shiftName?: string;
  }>;
  if (!shifts || shifts.length === 0) {
    return [
      {
        ...ShiftDetails.others[0],
        daysToAdd: 0,
        endTimeHour: ShiftDetails.others[0].endHour,
      },
    ];
  }

  return shifts.map(({ startHour, endHour, shiftName }) => {
    const daysToAdd = Math.floor(endHour / 24);
    const endTimeHour = endHour % 24;
    return {
      startHour,
      endHour,
      daysToAdd,
      endTimeHour,
      shiftName,
    };
  });
};

export const generateShiftData = (
  startDateStr: string,
  shiftDetails: ShiftDetail[],
  timezone: string
): Array<{ startDate: string; endDate: string; shiftName?: string }> => {
  const shifts = [];

  // Split a fractional hour (e.g. 19.5) into whole hours and minutes (19, 30).
  const splitHour = (hour: number) => {
    const whole = Math.floor(hour);
    const minute = Math.round((hour - whole) * 60);
    return { whole, minute };
  };

  for (const shift of shiftDetails) {
    const start = splitHour(shift.startHour);
    const end = splitHour(shift.endTimeHour);
    // Calculate start date and time in the given timezone
    const shiftStart = dayjs
      .tz(`${startDateStr} 00:00:00`, timezone)
      .hour(start.whole)
      .minute(start.minute)
      .second(0);
    // Calculate end date and time by adding days and setting end hour in the given timezone
    const shiftEnd = dayjs
      .tz(`${startDateStr} 00:00:00`, timezone)
      .add(shift.daysToAdd, 'day')
      .hour(end.whole)
      .minute(end.minute)
      .second(0);

    // Format dates as ISO strings for backend requests
    shifts.push({
      startDate: shiftStart.toISOString(),
      endDate: shiftEnd.toISOString(),
      shiftName: shift.shiftName,
    });
  }

  return shifts;
};

export const calculateUtilCurrentDailyAverage = (chartData: any[]) => {
  // if the chartData is empty, return 0, else extract seven day working minutes and return the average
  if (chartData?.length > 0) {
    const sevenDayData = chartData
      ?.filter((data) => data?.period === '7days')
      ?.map((data) => +data?.total_engine_working_minutes);

    const sevenDayTotalWorkingMinutes = sevenDayData.reduce(
      (acc: number, curr: number) => acc + curr,
      0
    );

    return sevenDayTotalWorkingMinutes / 7;
  } else {
    return 0;
  }
};

export const isAllValuesZero = (obj: Record<string, unknown>) => {
  const values = Object.values(obj);

  return values.every((value) => value === 0);
};

// Raw row/series identifiers used as internal lookup keys (matched against
// dataList/allData maps, pie-slice selection, and direction/color logic)
// must stay untranslated. This map resolves the raw identifier to a
// translation key ONLY for display purposes.
export const UTILIZATION_ROW_LABEL_KEYS: Record<string, string> = {
  'Engine Hours': 'engine_hours',
  'Engine Off Hours': 'engine_off_hours',
  'Productive Hours': 'productive_hours',
  'Idling Hours': 'idling_hours',
  Unknown: 'unknown',
  'Engine Off': 'engine_off_2',
  Productive: 'productive',
  Idle: 'idle',
};

export const getUtilizationRowLabel = (
  t: (key: string, options?: Record<string, unknown>) => string,
  name?: string | null
): string => {
  if (!name) return '';
  const key = UTILIZATION_ROW_LABEL_KEYS[name];
  return key ? t(key) : name;
};

// Raw option identifiers driving dropdown selection state and chart source
// lookups (see constants.ts) must stay untranslated. This map resolves the
// raw identifier to a translation key ONLY for dropdown display purposes.
export const UTILIZATION_OPTION_LABEL_KEYS: Record<string, string> = {
  Production: 'production',
  Utilization: 'dm_kpi_utilization',
  Fuel: 'dm_map_fuel',
  'Avg Fuel Rate': 'avg_fuel_rate',
  'Engine Hours': 'engine_hours',
  'Idling Hours': 'idling_hours',
  'Productive Hours': 'productive_hours',
  'Least Utilization': 'utilization_sort_least',
  'Most Utilization': 'utilization_sort_most',
  'Last 7 Days': 'last_7_days',
  'Last 14 Days': 'utilization_last_14_days',
  'Last 4 Weeks': 'utilization_last_4_weeks',
  'Last 6 Months': 'utilization_last_6_months',
};

export const getUtilizationOptionLabel = (
  t: (key: string, options?: Record<string, unknown>) => string,
  option?: string | null
): string => {
  if (!option) return '';
  const key = UTILIZATION_OPTION_LABEL_KEYS[option];
  return key ? t(key) : option;
};
